import { useContext } from 'react';
import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
} from 'react-hook-form';

import { Button } from 'components/Button';
import { DatePicker } from 'components/DatePicker';
import { Label } from 'components/Label';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { Divider } from 'components/layout';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { CarrierPointSubmitAction } from 'lib/hosts/quoting/carrierpoint';
import { QuotingPortal } from 'lib/hosts/quoting/interface';
import { NavisphereCarrierSubmitAction } from 'lib/hosts/quoting/navisphere-carrier';
import { QuoteValueType } from 'pages/QuoteView/Quoting/RequestQuickQuote/constants';
import { Maybe, Undef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { copyToClipboard } from 'utils/copyToClipboard';
import { formatCurrency } from 'utils/formatCurrency';
import { mapCustomerToSelectOptions } from 'utils/loadInfoAndBuilding';

import { CreatedQuoteMessage } from '../common/CreatedQuoteMessage';
import { QuickQuoteTextInput } from '../common/QuickQuoteTextInput';
import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import type {
  PortalSubmitInlineFeedback,
  SubmitPortalOverrides,
} from '../hooks/useQuoteHandlers';
import { QuickQuoteInputs } from '../types';

interface QuoteSubmissionSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  showSubmitToPortalButtons: boolean;
  portalInfo: Maybe<string>;
  isSubmittingToPortal: boolean;
  fuelEstimate: number;
  newCustomerURLInTMS: Maybe<string>;
  quoteURLInTMS: Maybe<string>;
  tmsName: Maybe<TMS>;
  portal: Undef<QuotingPortal>;
  handleSubmitToPortal: (
    quote: Maybe<number>,
    quoteValueType: QuoteValueType,
    overrides?: SubmitPortalOverrides,
    inlineFeedback?: PortalSubmitInlineFeedback
  ) => Promise<void>;
  handleSubmitQuoteToTMS: () => Promise<void>;
  handleQuoteSubmissionViaURL: () => void;
}

export const QuoteSubmissionSection = ({
  formMethods,
  showSubmitToPortalButtons,
  portalInfo,
  isSubmittingToPortal,
  fuelEstimate,
  newCustomerURLInTMS,
  quoteURLInTMS,
  tmsName,
  portal,
  handleSubmitToPortal,
  handleSubmitQuoteToTMS,
  handleQuoteSubmissionViaURL,
}: QuoteSubmissionSectionProps) => {
  const {
    control,
    formState: { errors },
  } = formMethods;

  // ✅ Consume from context instead of props
  const context = useQuickQuoteContext();
  const {
    hasThirdPartyQuoteURLs,
    submitQuoteLoading,
    loadingDraftReply,
    finalFlatPrice,
    selectedQuote,
    parentQuoteRequestId,
    customers,
    customersLoading,
    createdQuoteId,
    handleRefreshCustomers,
    handleResetCustomerSearch,
    handleCustomerSearch,
  } = context;

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  const {
    serviceFeaturesEnabled: {
      isQuoteSubmissionViaURLEnabled,
      isTMSQuoteSubmissionEnabled,
    },
    quickQuoteConfig,
  } = useServiceFeatures();

  return (
    <>
      {/* Submit quote via third-party URL (e.g. shipper TMSes) */}
      {isQuoteSubmissionViaURLEnabled && hasThirdPartyQuoteURLs && (
        <>
          <Typography
            variant='h5'
            weight='semibold'
            className='text-neutral-800 whitespace-nowrap'
          >
            Submission Hyperlink Detected
          </Typography>
          <div className='w-full mt-4'>
            <QuickQuoteTextInput
              name='quoteNumber'
              placeholder='Q000001'
              label='Quote #'
              required
            />
          </div>
          <Flex justify='between' gap='sm' className='w-full mt-2'>
            <Flex direction='col' className='flex-1'>
              <Label name='quoteExpirationDate'>Expiration</Label>
              <Controller
                name={`quoteExpirationDate`}
                control={control}
                render={({ field }: { field: ControllerRenderProps<any> }) => (
                  <DatePicker field={field} />
                )}
              />
              {errors.quoteExpirationDate && (
                <Typography variant='body-xs' className='text-error-500'>
                  {errors.quoteExpirationDate.message}
                </Typography>
              )}
            </Flex>
            <Flex direction='col' className='flex-1'>
              <Label name='Eta'>ETA</Label>
              <Controller
                name={`quoteEta`}
                control={control}
                render={({ field }: { field: ControllerRenderProps<any> }) => (
                  <DatePicker field={field} />
                )}
              />
              {errors.quoteEta && (
                <Typography variant='body-xs' className='text-error-500'>
                  {errors.quoteEta.message}
                </Typography>
              )}
            </Flex>
          </Flex>

          <Button
            className='w-full h-8 text-sm mt-4'
            type='button'
            buttonNamePosthog={ButtonNamePosthog.SubmitQuoteViaURL}
            disabled={loadingDraftReply}
            onClick={handleQuoteSubmissionViaURL}
          >
            {loadingDraftReply ? (
              <ButtonLoader />
            ) : (
              ButtonText.SubmitQuoteViaURL
            )}
          </Button>
        </>
      )}

      {/* Submit to Portal buttons if on an integrated portal */}
      {showSubmitToPortalButtons && (
        <Flex gap='sm' align='center' className='mt-4 w-full'>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='flex-1'>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.SubmitLineHaulToPortal}
                    logProperties={{
                      portal: (clickedSuggestion as any)?.source,
                      selectedQuote: selectedQuote,
                      quoteRequestId: parentQuoteRequestId,
                    }}
                    className='w-full bg-neutral-50 text-brand-main border border-brand-300 hover:bg-brand-50'
                    type='button'
                    disabled={isSubmittingToPortal || !finalFlatPrice}
                    onClick={() =>
                      handleSubmitToPortal(
                        finalFlatPrice ? finalFlatPrice - fuelEstimate : null,
                        QuoteValueType.Linehaul
                      )
                    }
                  >
                    {isSubmittingToPortal ? (
                      <ButtonLoader />
                    ) : (
                      `${quickQuoteConfig?.isSubmitOnPortalEnabled ? 'Submit' : 'Input'} line haul`
                    )}
                  </Button>
                </div>
              </TooltipTrigger>
              {!finalFlatPrice && (
                <TooltipContent>
                  <Typography>Unable to submit on this page</Typography>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className='flex-1'>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.SubmitTotalToPortal}
                    logProperties={{
                      portal: (clickedSuggestion as any)?.source,
                      selectedQuote: selectedQuote,
                      quoteRequestId: parentQuoteRequestId,
                    }}
                    className='w-full'
                    type='button'
                    disabled={isSubmittingToPortal || !finalFlatPrice}
                    onClick={() => {
                      // NOTE: Some portals only support line haul; total sell maps to line haul with warning
                      const forcesLineHaul =
                        portal?.submitAction ===
                          NavisphereCarrierSubmitAction ||
                        portal?.submitAction === CarrierPointSubmitAction;

                      if (forcesLineHaul) {
                        // Send as TotalSell to surface warning, but fill line-haul value
                        handleSubmitToPortal(
                          finalFlatPrice ? finalFlatPrice - fuelEstimate : null,
                          QuoteValueType.TotalSell
                        );
                      } else {
                        // NOTE: All other portals allow total sell to be submitted
                        handleSubmitToPortal(
                          finalFlatPrice,
                          QuoteValueType.TotalSell
                        );
                      }
                    }}
                    variant='default'
                  >
                    {isSubmittingToPortal ? (
                      <ButtonLoader />
                    ) : (
                      `${quickQuoteConfig?.isSubmitOnPortalEnabled ? 'Submit' : 'Input'} total sell`
                    )}
                  </Button>
                </div>
              </TooltipTrigger>
              {!finalFlatPrice && (
                <TooltipContent>
                  <Typography>Unable to submit on this page</Typography>
                </TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </Flex>
      )}

      {/* Show copy buttons if we are on a portal but not on a submittable page (e.g. CarrierDesk Load Info) */}
      {portal && !showSubmitToPortalButtons && (
        <Flex gap='sm' align='center' className='mt-4 w-full'>
          <Button
            buttonNamePosthog={
              ButtonNamePosthog.CopyLineHaulToClipboardFromPortal
            }
            onClick={() =>
              copyToClipboard(
                formatCurrency(
                  finalFlatPrice ? finalFlatPrice - fuelEstimate : 0,
                  'USD'
                )
              )
            }
            className='flex-1 bg-neutral-50 text-brand-main border border-brand-300 hover:bg-brand-50'
            disabled={!finalFlatPrice}
          >
            Copy line haul
          </Button>
          <Button
            buttonNamePosthog={
              ButtonNamePosthog.CopyTotalSellToClipboardFromPortal
            }
            onClick={() =>
              copyToClipboard(formatCurrency(finalFlatPrice ?? 0, 'USD'))
            }
            className='flex-1'
            disabled={!finalFlatPrice}
          >
            Copy total sell
          </Button>
        </Flex>
      )}

      {portalInfo && (
        <div className='w-full mt-4 p-3 bg-brand-50 border border-brand-200 rounded-lg'>
          <Flex gap='sm' align='start'>
            <Typography variant='body-sm' className='text-brand-800'>
              {portalInfo}
            </Typography>
          </Flex>
        </div>
      )}

      {/* TMS Submission */}
      {isTMSQuoteSubmissionEnabled && (
        <>
          <Divider variant='strong' spacing='md' className='my-6' />

          <Typography
            variant='h5'
            weight='semibold'
            className='text-neutral-800 whitespace-nowrap'
          >
            Save quote in TMS
          </Typography>

          <div className='mt-4'>
            <RHFDebounceSelect
              required={false}
              name='customerName'
              label='Customer'
              control={control}
              errors={errors}
              data={customers}
              isLoading={customersLoading}
              showSearchParamDropdown={false}
              refreshHandler={handleRefreshCustomers}
              resetOptionsHandler={handleResetCustomerSearch}
              fetchOptions={handleCustomerSearch}
              mapOptions={mapCustomerToSelectOptions}
            />
            {newCustomerURLInTMS && (
              <a
                className='underline text-[12px] text-brand'
                target='_blank'
                rel='noreferrer'
                href={newCustomerURLInTMS}
              >
                or create a new customer.
              </a>
            )}
          </div>

          <CreatedQuoteMessage
            createdQuoteId={createdQuoteId}
            quoteURLInTMS={quoteURLInTMS}
            showWithEmptyQuoteId={tmsName === TMS.ThreeG}
          />

          <Button
            className='w-full h-8 text-sm mt-4'
            type='button'
            buttonNamePosthog={ButtonNamePosthog.SubmitQuoteToTMS}
            disabled={submitQuoteLoading}
            onClick={async () => await handleSubmitQuoteToTMS()}
          >
            {submitQuoteLoading ? (
              <ButtonLoader />
            ) : (
              ButtonText.SubmitQuoteToTMS
            )}
          </Button>
        </>
      )}
    </>
  );
};
